import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Alert, Button, FormRow } from 'components/common';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import CreatableSelect from 'react-select/creatable';
import PropTypes from 'prop-types';
import { closeLostProject, lostReasonProject, createLostReason } from 'redux/crm/projectSlice';
import { getProjectBuildings } from 'redux/crm/project/quotationSlice';
import { SELECT_STYLES } from 'helpers/formStyles';

const CloseLostForm = ({ showModal, setshowModal, project }) => {
  const { control, handleSubmit, formState: { errors }, setValue } = useForm();
  const [selectedIds, setSelectedIds] = useState([]);
  const [buildingData, setBuildingData] = useState([]);
  const [lostOptions, setLostOptions] = useState([]);
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const hasBuildings = project?.total_buildings > 0;
  const dispatch = useDispatch();

  const fetchBuildings = useCallback(async () => {
    const response = await dispatch(getProjectBuildings({ projectId: project.id }));
    const rows = response.payload?.rows;
    if (rows) {
      setBuildingData(rows.map(item => ({ id: item.attributes.id, name: item.attributes.name })));
    }
  }, [dispatch, project.id]);

  const fetchLostReason = useCallback(async () => {
    setIsLoadingReasons(true);
    try {
      const response = await dispatch(lostReasonProject());
      const data = response.payload;
      const options = Array.isArray(data) ? data.map(item => ({ value: item.value, label: item.value })) : [];
      setLostOptions(options);
    } finally {
      setIsLoadingReasons(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchBuildings();
    fetchLostReason();
  }, [fetchBuildings, fetchLostReason]);

  useEffect(() => {
    setSelectedIds(buildingData.map(item => item.id));
  }, [buildingData]);

  const handleCreateReason = async (inputValue) => {
    setIsCreating(true);
    try {
      const result = await dispatch(createLostReason({ value: inputValue })).unwrap();
      const reasonValue = result.value || inputValue;
      const newOption = { value: reasonValue, label: reasonValue };
      setLostOptions(prev => [...prev, newOption]);
      setValue('reason', reasonValue, { shouldValidate: true });
    } catch {
      toast.error('Failed to create new reason');
    } finally {
      setIsCreating(false);
    }
  };

  const closeModal = () => setshowModal(false);

  const toggleBuilding = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAll = (all) => {
    setSelectedIds(all ? buildingData.map(item => item.id) : []);
  };

  const allSelected = buildingData.length > 0 && selectedIds.length === buildingData.length;

  const onSubmit = async (data) => {
    try {
      const payload = { proj_ids: hasBuildings ? selectedIds : null, reason: data.reason };
      await dispatch(closeLostProject({ projectId: project.id, data: payload }));
      closeModal();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const footer = (
    <>
      <Button type="submit" size="sm" onClick={handleSubmit(onSubmit)}>Update to Lost</Button>
      <Button variant="danger" size="sm" onClick={closeModal}>Cancel</Button>
    </>
  );

  return (
    <Modal
      show={showModal}
      onHide={closeModal}
      title="Update to Close Lost"
      backdropClick={false}
      footer={footer}
    >
      <Alert type="warning" className="tw-mb-4">
        Specify the lost buildings for the project <strong>{project.name}</strong>.
      </Alert>

      {hasBuildings && (
        <div className="tw-mb-4">
          {/* Select all header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-widest tw-text-slate-500">
              Buildings
            </span>
            <label className="tw-flex tw-items-center tw-gap-1.5 tw-cursor-pointer tw-text-xs tw-text-slate-600">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={e => handleSelectAll(e.target.checked)}
                className="tw-rounded-sm tw-border-slate-300 tw-accent-blue-600"
              />
              Select all
            </label>
          </div>

          {/* Building grid */}
          <div className="tw-grid tw-grid-cols-3 tw-gap-2">
            {buildingData.map(item => (
              <label
                key={item.id}
                className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded tw-border tw-cursor-pointer tw-text-xs tw-transition-colors ${
                  selectedIds.includes(item.id)
                    ? 'tw-bg-blue-50 tw-border-blue-400 tw-text-blue-700 tw-font-semibold'
                    : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-blue-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(item.id)}
                  onChange={() => toggleBuilding(item.id)}
                  className="tw-accent-blue-600"
                />
                {item.name}
              </label>
            ))}
          </div>

          {errors.proj_ids && (
            <p className="tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0">{errors.proj_ids.message}</p>
          )}

          {/* Hidden controller to register field for validation */}
          <Controller
            control={control}
            name="proj_ids"
            rules={{
              validate: () => {
                if (hasBuildings && selectedIds.length === 0) return 'Building must be required.';
                return true;
              },
            }}
            render={() => null}
          />
        </div>
      )}

      <FormRow label="Reason" required error={errors.reason?.message}>
        <Controller
          control={control}
          name="reason"
          rules={{ required: 'Reason must be required' }}
          render={({ field }) => (
            <CreatableSelect
              closeMenuOnSelect
              options={lostOptions}
              placeholder={isLoadingReasons ? 'Loading reasons…' : 'Select or type to create…'}
              menuPosition="fixed"
              styles={SELECT_STYLES}
              isLoading={isLoadingReasons || isCreating}
              isDisabled={isLoadingReasons || isCreating}
              value={lostOptions.find(opt => opt.value === field.value) || null}
              onChange={opt => setValue('reason', opt?.value, { shouldValidate: true })}
              onCreateOption={handleCreateReason}
              formatCreateLabel={(input) => `Create new reason: "${input}"`}
            />
          )}
        />
        <p className="tw-text-xs tw-text-slate-400 tw-mt-1.5 tw-mb-0">
          Can&apos;t find the reason? Type it in the field above and select <strong className="tw-text-slate-500">Create new reason</strong> to add it.
        </p>
      </FormRow>
    </Modal>
  );
};

CloseLostForm.propTypes = {
  showModal:    PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  project:      PropTypes.object.isRequired,
};

export default CloseLostForm;
